import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useActionEditorDraft } from '../../hooks/useActionEditorDraft';
import {
  buildEmailActionDraftFromConfig,
  buildEmailActionDraftFromStep,
} from '../../utils/actionEditorDraftUtils';
import {
  X,
  Mail,
  User,
  Save,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  Info,
  Link2,
  Eye,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Sparkles,
  Search,
  ChevronDown,
  ChevronRight,
  Settings,
  Send,
  BarChart3,
  MousePointerClick,
  ExternalLink,
  Tag,
  Trash2,
  Undo,
  Redo,
  Image,
  Link,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  MailOpen,
  Reply,
  Ban,
  Loader2,
  Edit3,
  Monitor,
  Smartphone,
  Paperclip,
  FileText,
  Code,
  Type,
  Palette,
  Highlighter,
  LayoutTemplate,
  Wand2,
  Zap,
  Building2,
  DollarSign,
  Globe,
  Key,
  CreditCard,
  Hash,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import WriteEmailWithAIModal from './WriteEmailWithAIModal';
import RichEmailEditor from './RichEmailEditor';
import MergeFieldInput from './MergeFieldInput';
import WorkflowEditorPanelShell from './WorkflowEditorPanelShell';
import { formatEmailForEditor } from '../../utils/writeEmailAIUtils';
import { collectAllMergeTokens } from '../../utils/automationMergeFields';
import { countWords, stripHtmlForCount } from '../../utils/richEmailEditorUtils';
import {
  formatTestEmailErrorMessage,
  formatTestEmailSuccessMessage,
  isAutomationTestEmailSuccess,
  parseTestEmailInput,
  sendAutomationTestEmail,
  validateTestEmailInput,
} from '../../utils/automationTestEmailApi';
import EmailStatisticsDashboard, {
  EmailStatisticsDetailsModal,
} from './EmailStatisticsDashboard';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');

// ============================================
// VARIABLE CATEGORIES FOR PICKER
// ============================================
const VARIABLE_CATEGORIES = [
  {
    id: 'contact',
    name: 'Contact',
    icon: User,
    color: 'blue',
    variables: [
      { token: '{{contact.first_name}}', label: 'First Name', description: 'Contact first name' },
      { token: '{{contact.last_name}}', label: 'Last Name', description: 'Contact last name' },
      { token: '{{contact.full_name}}', label: 'Full Name', description: 'Contact full name' },
      { token: '{{contact.email}}', label: 'Email', description: 'Contact email address' },
      { token: '{{contact.phone}}', label: 'Phone', description: 'Contact phone number' },
      { token: '{{contact.company_name}}', label: 'Company', description: 'Contact company name' },
      { token: '{{contact.street_address}}', label: 'Street Address', description: 'Contact street address' },
      { token: '{{contact.city}}', label: 'City', description: 'Contact city' },
      { token: '{{contact.state}}', label: 'State', description: 'Contact state' },
      { token: '{{contact.zip_code}}', label: 'ZIP Code', description: 'Contact ZIP code' },
      { token: '{{contact.status}}', label: 'Status', description: 'Contact status' },
      { token: '{{contact.created_date}}', label: 'Created Date', description: 'When contact was created' },
    ],
  },
  {
    id: 'agency',
    name: 'Agency',
    icon: Building2,
    color: 'emerald',
    variables: [
      { token: '{{agency.name}}', label: 'Agency Name', description: 'Your agency name' },
      { token: '{{agency.email}}', label: 'Agency Email', description: 'Agency email address' },
      { token: '{{agency.phone}}', label: 'Agency Phone', description: 'Agency phone number' },
      { token: '{{agency.website}}', label: 'Agency Website', description: 'Agency website URL' },
      { token: '{{agency.owner_name}}', label: 'Owner Name', description: 'Agency owner name' },
      { token: '{{agency.address}}', label: 'Agency Address', description: 'Agency address' },
      { token: '{{agency.logo_url}}', label: 'Logo URL', description: 'Agency logo URL' },
    ],
  },
  {
    id: 'platform',
    name: 'Platform',
    icon: Globe,
    color: 'purple',
    variables: [
      { token: '{{platform.name}}', label: 'Platform Name', description: 'Platform name' },
      { token: '{{platform.support_email}}', label: 'Support Email', description: 'Platform support email' },
      { token: '{{platform.url}}', label: 'Platform URL', description: 'Platform URL' },
    ],
  },
  {
    id: 'portal',
    name: 'Portal',
    icon: Key,
    color: 'cyan',
    variables: [
      { token: '{{portal.login_url}}', label: 'Login URL', description: 'Portal login link' },
      { token: '{{portal.upload_url}}', label: 'Upload URL', description: 'Document upload link' },
      { token: '{{reset_link}}', label: 'Reset Password Link', description: 'Password reset link' },
      { token: '{{verification_link}}', label: 'Verification Link', description: 'Email verification link' },
      { token: '{{temp_password}}', label: 'Temp Password', description: 'Temporary password' },
    ],
  },
  {
    id: 'whop',
    name: 'Whop',
    icon: CreditCard,
    color: 'orange',
    variables: [
      { token: '{{whop.product_name}}', label: 'Product Name', description: 'Whop product name' },
      { token: '{{whop.plan_name}}', label: 'Plan Name', description: 'Whop plan name' },
      { token: '{{payment.update_url}}', label: 'Update Payment URL', description: 'Payment update link' },
      { token: '{{upgrade_url}}', label: 'Upgrade URL', description: 'Upgrade link' },
      { token: '{{reactivate_url}}', label: 'Reactivate URL', description: 'Reactivation link' },
    ],
  },
  {
    id: 'opportunity',
    name: 'Opportunity',
    icon: DollarSign,
    color: 'amber',
    variables: [
      { token: '{{opportunity.name}}', label: 'Opportunity Name', description: 'Opportunity name' },
      { token: '{{opportunity.value}}', label: 'Value', description: 'Opportunity value' },
      { token: '{{opportunity.stage}}', label: 'Stage', description: 'Current stage' },
      { token: '{{opportunity.status}}', label: 'Status', description: 'Opportunity status' },
    ],
  },
  {
    id: 'billing',
    name: 'Billing',
    icon: CreditCard,
    color: 'green',
    variables: [
      { token: '{{billing.amount}}', label: 'Amount', description: 'Billing amount' },
      { token: '{{billing.due_date}}', label: 'Due Date', description: 'Payment due date' },
      { token: '{{billing.invoice_link}}', label: 'Invoice Link', description: 'Invoice link' },
      { token: '{{billing.plan_name}}', label: 'Plan Name', description: 'Subscription plan name' },
    ],
  },
  {
    id: 'system',
    name: 'System',
    icon: Clock,
    color: 'slate',
    variables: [
      { token: '{{now.date}}', label: 'Current Date', description: 'Today\'s date' },
      { token: '{{now.time}}', label: 'Current Time', description: 'Current time' },
      { token: '{{now.datetime}}', label: 'Date & Time', description: 'Current date and time' },
      { token: '{{now.day_of_week}}', label: 'Day of Week', description: 'Current day of week' },
      { token: '{{unsubscribe_url}}', label: 'Unsubscribe URL', description: 'Unsubscribe link' },
    ],
  },
];

// ============================================
// TRIGGER LINKS CONFIGURATION
// ============================================
const TRIGGER_LINKS = [
  { id: 'portal_login', label: 'Portal Login Link', token: '{{portal.login_url}}', description: 'Link to client portal login' },
  { id: 'upload_report', label: 'Upload Credit Report', token: '{{portal.upload_url}}', description: 'Link to upload credit report' },
  { id: 'payment_update', label: 'Update Payment Method', token: '{{payment.update_url}}', description: 'Link to update payment' },
  { id: 'reset_password', label: 'Reset Password', token: '{{reset_link}}', description: 'Password reset link' },
  { id: 'verify_email', label: 'Verify Email', token: '{{verification_link}}', description: 'Email verification link' },
  { id: 'schedule_call', label: 'Schedule Consultation', token: '{{schedule_url}}', description: 'Schedule a call' },
  { id: 'upgrade', label: 'Upgrade Account', token: '{{upgrade_url}}', description: 'Account upgrade link' },
  { id: 'unsubscribe', label: 'Unsubscribe', token: '{{unsubscribe_url}}', description: 'Email unsubscribe link' },
];

// ============================================
// EMAIL TEMPLATES FOR "SELECT TEMPLATE"
// ============================================
const EMAIL_TEMPLATES = [
  { id: 'welcome', name: 'Welcome Email', category: 'Onboarding', description: 'Welcome new clients/users' },
  { id: 'document_request', name: 'Document Request', category: 'Operations', description: 'Request documents from client' },
  { id: 'payment_reminder', name: 'Payment Reminder', category: 'Billing', description: 'Remind about upcoming payment' },
  { id: 'payment_failed', name: 'Payment Failed', category: 'Billing', description: 'Notify about failed payment' },
  { id: 'dispute_update', name: 'Dispute Status Update', category: 'Operations', description: 'Update on dispute progress' },
  { id: 'portal_invite', name: 'Portal Invitation', category: 'Onboarding', description: 'Invite to client portal' },
  { id: 'appointment_confirm', name: 'Appointment Confirmation', category: 'Operations', description: 'Confirm scheduled appointment' },
  { id: 'cancellation', name: 'Cancellation Notice', category: 'Lifecycle', description: 'Account cancellation notification' },
];

// ============================================
// SAMPLE DATA FOR PREVIEW
// ============================================
const SAMPLE_PREVIEW_DATA = {
  'contact.first_name': 'John',
  'contact.last_name': 'Doe',
  'contact.full_name': 'John Doe',
  'contact.email': 'john@example.com',
  'contact.phone': '(555) 123-4567',
  'contact.company_name': 'Acme Inc.',
  'platform.name': 'FundElite',
  'platform.support_email': 'support@fundelite.ai',
  'platform.url': 'https://app.fundelite.ai',
  'agency.name': 'Credit Repair Pros',
  'agency.email': 'info@creditrepairpros.com',
  'agency.owner_name': 'Michael Smith',
  'portal.login_url': 'https://app.fundelite.ai/login',
  'portal.upload_url': 'https://app.fundelite.ai/upload',
  'reset_link': 'https://app.fundelite.ai/reset-password/abc123',
  'verification_link': 'https://app.fundelite.ai/verify/xyz789',
  'temp_password': 'TempPass123!',
  'opportunity.name': 'Self Repair Intake',
  'whop.product_name': 'CredFortune Accelerator',
  'whop.plan_name': 'Monthly Membership',
  'payment.update_url': 'https://app.fundelite.ai/billing/update',
  'upgrade_url': 'https://app.fundelite.ai/upgrade',
  'unsubscribe_url': 'https://app.fundelite.ai/unsubscribe',
  'now.date': new Date().toLocaleDateString(),
  'now.time': new Date().toLocaleTimeString(),
  'billing.amount': '$79.00',
  'billing.due_date': 'May 15, 2026',
};

// Substitute variables with sample data
const substituteVariables = (content) => {
  if (!content) return '';
  let result = content;
  Object.entries(SAMPLE_PREVIEW_DATA).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${key.replace('.', '\\.')}\\s*\\}\\}`, 'gi');
    result = result.replace(regex, value);
  });
  // Highlight remaining unmatched variables
  result = result.replace(/\{\{([^}]+)\}\}/g, '<span style="background:#fef3c7;color:#92400e;padding:2px 6px;border-radius:4px;font-size:12px;">$1</span>');
  return result;
};

// Sanitize HTML for safe rendering
const sanitizeHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
    .replace(/javascript:/gi, '');
};

// ============================================
// VARIABLE PICKER COMPONENT
// ============================================
const VariablePicker = ({ isOpen, onClose, onInsert, position = 'bottom' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('contact');
  const [copiedToken, setCopiedToken] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredCategories = VARIABLE_CATEGORIES.map(cat => ({
    ...cat,
    variables: cat.variables.filter(v =>
      v.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.token.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => searchQuery === '' || cat.variables.length > 0);

  const handleInsert = (token) => {
    onInsert(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1000);
  };

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <div 
      ref={pickerRef}
      className={cn(
        "absolute z-[100] bg-white border border-slate-200 rounded-xl shadow-2xl w-96 max-h-[480px] flex flex-col",
        position === 'bottom' ? 'top-full mt-2 left-0' : 'bottom-full mb-2 left-0'
      )}
      data-testid="variable-picker"
    >
      {/* Header */}
      <div className="p-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
            <Tag size={14} className="text-blue-500" />
            Custom Values / Variables
          </h4>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X size={14} className="text-slate-400" />
          </button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm bg-white"
            autoFocus
          />
        </div>
      </div>

      {/* Categories & Variables */}
      <div className="flex-1 overflow-y-auto">
        {filteredCategories.map((category) => {
          const isExpanded = expandedCategory === category.id || searchQuery !== '';
          const IconComponent = category.icon;
          
          return (
            <div key={category.id} className="border-b border-slate-100 last:border-0">
              <button
                onClick={() => setExpandedCategory(isExpanded && searchQuery === '' ? null : category.id)}
                className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight 
                  size={14} 
                  className={cn(
                    "text-slate-400 transition-transform",
                    isExpanded && "rotate-90"
                  )} 
                />
                <div className={cn(
                  'w-6 h-6 rounded flex items-center justify-center',
                  colorClasses[category.color]
                )}>
                  <IconComponent size={12} />
                </div>
                <span className="text-sm font-medium text-slate-700">{category.name}</span>
                <Badge variant="outline" className="ml-auto text-[10px] bg-white">
                  {category.variables.length}
                </Badge>
              </button>
              
              {isExpanded && (
                <div className="pb-2 bg-slate-50/50">
                  {category.variables.map((variable) => (
                    <button
                      key={variable.token}
                      onClick={() => handleInsert(variable.token)}
                      className="w-full flex items-center gap-2 px-3 py-2 pl-11 hover:bg-blue-50 text-left group transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 font-medium">{variable.label}</p>
                        <p className="text-xs text-slate-500 truncate">{variable.token}</p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {copiedToken === variable.token ? (
                          <CheckCircle2 size={14} className="text-emerald-500" />
                        ) : (
                          <span className="text-xs text-blue-500 font-medium">Insert</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-2 border-t border-slate-200 bg-slate-50 rounded-b-xl">
        <p className="text-xs text-slate-500 text-center">
          Click to insert at cursor position
        </p>
      </div>
    </div>
  );
};

// ============================================
// TRIGGER LINKS PICKER COMPONENT
// ============================================
const TriggerLinksPicker = ({ isOpen, onClose, onInsert }) => {
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={pickerRef}
      className="absolute z-[100] bg-white border border-slate-200 rounded-xl shadow-2xl w-80 top-full mt-2 left-0"
      data-testid="trigger-links-picker"
    >
      <div className="p-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
            <Zap size={14} className="text-amber-500" />
            Trigger Links
          </h4>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X size={14} className="text-slate-400" />
          </button>
        </div>
      </div>
      
      <div className="max-h-[300px] overflow-y-auto p-2">
        {TRIGGER_LINKS.map((link) => (
          <button
            key={link.id}
            onClick={() => {
              onInsert(link.token);
              onClose();
            }}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-blue-50 text-left group transition-colors"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Link2 size={14} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700 font-medium">{link.label}</p>
              <p className="text-xs text-slate-500 truncate">{link.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// TEMPLATE PICKER COMPONENT
// ============================================
const TemplatePicker = ({ isOpen, onClose, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredTemplates = EMAIL_TEMPLATES.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    acc[template.category].push(template);
    return acc;
  }, {});

  return (
    <div 
      ref={pickerRef}
      className="absolute z-[100] bg-white border border-slate-200 rounded-xl shadow-2xl w-96 top-full mt-2 left-0"
      data-testid="template-picker"
    >
      <div className="p-3 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
            <LayoutTemplate size={14} className="text-purple-500" />
            Select Template
          </h4>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X size={14} className="text-slate-400" />
          </button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
            autoFocus
          />
        </div>
      </div>
      
      <div className="max-h-[350px] overflow-y-auto p-2">
        {Object.entries(groupedTemplates).map(([category, templates]) => (
          <div key={category} className="mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-2 mb-1">{category}</p>
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  onSelect(template);
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-purple-50 text-left group transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 font-medium">{template.name}</p>
                  <p className="text-xs text-slate-500 truncate">{template.description}</p>
                </div>
              </button>
            ))}
          </div>
        ))}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <LayoutTemplate size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No templates found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// EMAIL PREVIEW COMPONENT (SHARED)
// ============================================
const EmailPreviewPanel = ({ fromName, fromEmail, replyTo, subject, preheader, bodyHtml }) => {
  const [viewMode, setViewMode] = useState('desktop');

  const preview = useMemo(() => ({
    fromName: substituteVariables(fromName) || 'Your Company',
    fromEmail: substituteVariables(fromEmail) || 'noreply@example.com',
    replyTo: substituteVariables(replyTo),
    subject: substituteVariables(subject) || '(No subject)',
    preheader: substituteVariables(preheader),
    body: sanitizeHtml(substituteVariables(bodyHtml)),
  }), [fromName, fromEmail, replyTo, subject, preheader, bodyHtml]);

  const isEmpty = !bodyHtml || bodyHtml.trim() === '';

  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Preview Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-slate-900">Email Preview</span>
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Live</Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('desktop')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                viewMode === 'desktop' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
            >
              <Monitor size={14} />
              Desktop
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                viewMode === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
            >
              <Smartphone size={14} />
              Mobile
            </button>
          </div>
        </div>
      </div>
      
      {/* Preview Container */}
      <div className="flex-1 overflow-auto p-4">
        <div className={cn(
          'mx-auto transition-all duration-300',
          viewMode === 'desktop' ? 'max-w-[650px]' : 'max-w-[375px]'
        )}>
          {/* Email Client Shell */}
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-lg">
            {/* Email Header */}
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User size={18} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{preview.fromName}</span>
                    <span className="text-slate-500 text-sm">&lt;{preview.fromEmail}&gt;</span>
                  </div>
                  {preview.replyTo && (
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                      <Reply size={12} />
                      Reply-To: {preview.replyTo}
                    </div>
                  )}
                </div>
              </div>
              
              <h2 className="text-lg font-semibold text-slate-900 leading-tight mb-1">{preview.subject}</h2>
              {preview.preheader && (
                <p className="text-sm text-slate-500 leading-relaxed">{preview.preheader}</p>
              )}
            </div>
            
            {/* Email Body */}
            <div className="bg-white min-h-[200px]">
              {isEmpty ? (
                <div className="py-16 px-8 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Mail size={28} className="text-slate-400" />
                  </div>
                  <h3 className="text-slate-600 font-medium mb-2">No email content yet</h3>
                  <p className="text-slate-400 text-sm">Add content in the Compose tab to preview.</p>
                </div>
              ) : (
                <div 
                  className="p-6 prose prose-sm max-w-none"
                  style={{ fontSize: viewMode === 'mobile' ? '14px' : '16px', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: preview.body }}
                />
              )}
            </div>
          </div>
          
          {/* Preview Info */}
          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-500">
            <Badge variant="outline" className="text-[10px]">
              {viewMode === 'desktop' ? '650px' : '375px'} width
            </Badge>
            <span className="flex items-center gap-1.5">
              <AlertCircle size={12} />
              Variables shown with sample data
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EMAIL STATISTICS PANEL (ALWAYS-ON DASHBOARD)
// ============================================
const EmailStatisticsPanel = ({ stepId }) => {
  const [stats, setStats] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!stepId) {
      setStats(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const configRes = await fetch(`${API_URL}/api/webhooks/mailgun/config-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (configRes.ok) {
        setConfigStatus(await configRes.json());
      }

      const statsRes = await fetch(`${API_URL}/api/webhooks/mailgun/stats/${stepId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else {
        setStats(null);
        setError('Statistics could not be refreshed.');
      }
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
      setStats(null);
      setError('Statistics could not be refreshed.');
    } finally {
      setIsLoading(false);
    }
  }, [stepId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <>
      <EmailStatisticsDashboard
        variant="compact"
        stats={stats}
        isLoading={isLoading}
        error={error}
        onRetry={fetchStats}
        trackingReady={configStatus ? Boolean(configStatus.is_tracking_ready) : true}
        onOpenDetails={() => setDetailsOpen(true)}
      />
      <EmailStatisticsDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        stats={stats}
        isLoading={isLoading}
        error={error}
        onRetry={fetchStats}
      />
    </>
  );
};

// ============================================
// MAIN EMAIL ACTION COMPOSER COMPONENT
// ============================================
const EmailActionComposer = ({
  isOpen,
  onClose,
  step,
  onSave,
  channelStatus,
  onNavigateToSettings,
  workflowStatus = 'draft',
  workflowId,
  token,
  agencyId,
  scope = 'agency', // 'agency' or 'platform'
  width,
  overlayMode = false,
  onOverlayDismiss,
  activePanelType = 'email',
  chromeHeight,
}) => {
  // Main tabs: compose | preview
  const [activeTab, setActiveTab] = useState('compose');
  // Create mode: quick | smart | template
  const [createMode, setCreateMode] = useState('quick');
  
  // Pickers state
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [showTriggerLinks, setShowTriggerLinks] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  
  // Additional settings expanded
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  
  // Write with AI modal
  const [showWriteWithAIModal, setShowWriteWithAIModal] = useState(false);

  const availableVariables = useMemo(() => collectAllMergeTokens(), []);
  
  // Editor refs
  const richEditorRef = useRef(null);
  
  // Form state
  const [config, setConfig] = useState({
    actionName: 'Send Email',
    fromName: 'FundElite',
    fromEmail: '',
    replyTo: '',
    to: '{{contact.email}}',
    cc: '',
    bcc: '',
    subject: '',
    preheader: '',
    body: '',
    bodyHtml: '',
    templateId: '',
    trackClicks: true,
    utmTracking: false,
    utmSource: '',
    utmMedium: 'email',
    utmCampaign: '',
    addTags: [],
    tagInput: '',
    attachments: [],
  });

  // Initialize from step config
  useEffect(() => {
    if (step?.config) {
      setConfig(prev => ({
        ...prev,
        actionName: step.config.actionName || step.config.name || step.name || step.label || prev.actionName,
        fromName: step.config.fromName || step.config.from_name || prev.fromName,
        fromEmail: step.config.fromEmail || step.config.from_email || prev.fromEmail,
        replyTo: step.config.replyTo || step.config.reply_to || prev.replyTo,
        to: step.config.to || prev.to,
        cc: step.config.cc || '',
        bcc: step.config.bcc || '',
        subject: step.config.subject || '',
        preheader: step.config.preheader || '',
        body: step.config.body || step.config.body_html || '',
        bodyHtml: step.config.body_html || step.config.body || '',
        templateId: step.config.templateId || '',
        trackClicks: step.config.trackClicks !== false,
        utmTracking: step.config.utmTracking || false,
        utmSource: step.config.utmSource || '',
        utmMedium: step.config.utmMedium || 'email',
        utmCampaign: step.config.utmCampaign || '',
        addTags: step.config.addTags || [],
        attachments: step.config.attachments || [],
      }));
    }
  }, [step]);

  // Default from email from platform/agency settings when not configured on the step
  useEffect(() => {
    if (!isOpen || config.fromEmail) return;

    const authToken = token || localStorage.getItem('token');
    if (!authToken) return;

    const settingsPath = scope === 'platform'
      ? `${API_URL}/api/platform/settings/integrations/mailgun`
      : agencyId
        ? `${API_URL}/api/agency/${agencyId}/settings/mailgun`
        : null;
    if (!settingsPath) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(settingsPath, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const fromEmail = res.data?.from_email || res.data?.settings?.from_email || '';
        const fromName = res.data?.from_name || res.data?.settings?.from_name || '';
        if (!cancelled && fromEmail) {
          setConfig((prev) => ({
            ...prev,
            fromEmail: prev.fromEmail || fromEmail,
            fromName: prev.fromName || fromName || prev.fromName,
          }));
        }
      } catch {
        // Manual entry remains available when settings are unavailable
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, scope, agencyId, token, config.fromEmail]);

  const emailDraft = useMemo(
    () => buildEmailActionDraftFromConfig(config),
    [config],
  );

  const { guardedClose, commitDraft } = useActionEditorDraft({
    editorId: step ? `email-${step.id}` : 'email',
    isOpen,
    step,
    draft: emailDraft,
    mapStepToDraft: buildEmailActionDraftFromStep,
  });

  const handleRequestClose = useCallback(() => {
    guardedClose(onClose);
  }, [guardedClose, onClose]);

  const handleBodyChange = useCallback((html) => {
    setConfig((prev) => ({ ...prev, body: html, bodyHtml: html }));
  }, []);

  // Insert variable at cursor
  const insertVariable = useCallback((token) => {
    richEditorRef.current?.insertHtml(token);
    setShowVariablePicker(false);
    setShowTriggerLinks(false);
  }, []);

  // Handle template selection
  const handleTemplateSelect = useCallback((template) => {
    // Load template content - in real app would fetch from API
    const templateContent = {
      welcome: {
        subject: 'Welcome to {{agency.name}}!',
        preheader: 'Your credit repair journey begins now',
        body: `<p>Hi {{contact.first_name}},</p>
<p>Welcome to <strong>{{agency.name}}</strong>! We're thrilled to have you on board.</p>
<p>Your journey to better credit starts now. Here's what you can expect:</p>
<ul>
<li>Expert analysis of your credit report</li>
<li>Strategic dispute letters</li>
<li>Ongoing progress tracking</li>
</ul>
<p><a href="{{portal.login_url}}">Log in to your portal</a> to get started.</p>
<p>Best regards,<br>{{agency.owner_name}}</p>`,
      },
      payment_reminder: {
        subject: 'Friendly Payment Reminder',
        preheader: 'Your payment is coming up',
        body: `<p>Hi {{contact.first_name}},</p>
<p>This is a friendly reminder that your payment of {{billing.amount}} is due on {{billing.due_date}}.</p>
<p><a href="{{payment.update_url}}">Update your payment method</a> if needed.</p>
<p>Questions? Reply to this email or contact our support team.</p>
<p>Thank you,<br>{{agency.name}}</p>`,
      },
    };

    const content = templateContent[template.id];
    if (content) {
      setConfig(prev => ({
        ...prev,
        subject: content.subject,
        preheader: content.preheader,
        body: content.body,
        bodyHtml: content.body,
        templateId: template.id,
      }));
      if (richEditorRef.current) {
        richEditorRef.current.setHtml(content.body);
      }
    }
    setCreateMode('quick');
    toast.success(`Template "${template.name}" loaded`);
  }, []);

  const handleUseAIEmail = useCallback((generatedEmail) => {
    const content = formatEmailForEditor(generatedEmail, false);
    setConfig((prev) => ({
      ...prev,
      body: content,
      bodyHtml: content,
    }));

    richEditorRef.current?.setHtml(content);

    toast.success('AI email inserted');
  }, []);

  // Send test email
  const handleSendTestEmail = async () => {
    const validationError = validateTestEmailInput(testEmail);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const recipients = parseTestEmailInput(testEmail);
    const bodyHtml = config.body || config.bodyHtml || '';
    if (!bodyHtml.trim()) {
      toast.error('Please add email content before sending a test');
      return;
    }

    setIsSendingTest(true);
    try {
      const authToken = token || localStorage.getItem('token');
      const { response, data } = await sendAutomationTestEmail({
        apiBase: API_URL,
        token: authToken,
        recipients,
        subject: config.subject,
        bodyHtml,
        bodyText: stripHtmlForCount(bodyHtml),
        fromName: config.fromName,
        fromEmail: config.fromEmail,
        workflowId,
        actionId: step?.id || step?._id,
      });

      if (isAutomationTestEmailSuccess(response, data)) {
        toast.success(formatTestEmailSuccessMessage(data, recipients));
      } else {
        toast.error(formatTestEmailErrorMessage(data));
      }
    } catch (err) {
      console.error('Test email error:', err);
      toast.error('Failed to send test email. Please try again.');
    } finally {
      setIsSendingTest(false);
    }
  };

  // Add tag
  const handleAddTag = () => {
    if (config.tagInput.trim() && !config.addTags.includes(config.tagInput.trim())) {
      setConfig(prev => ({
        ...prev,
        addTags: [...prev.addTags, prev.tagInput.trim()],
        tagInput: '',
      }));
    }
  };

  // Save action
  const handleSave = () => {
    if (!config.subject?.trim()) {
      toast.error('Subject is required');
      return;
    }

    const bodyContent = config.body || config.bodyHtml || '';
    onSave({
      ...step,
      label: config.actionName || 'Send Email',
      name: config.actionName || 'Send Email',
      config: {
        name: config.actionName || 'Send Email',
        actionName: config.actionName || 'Send Email',
        fromName: config.fromName,
        from_name: config.fromName,
        fromEmail: config.fromEmail,
        from_email: config.fromEmail,
        replyTo: config.replyTo,
        reply_to: config.replyTo,
        to: config.to,
        cc: config.cc,
        bcc: config.bcc,
        subject: config.subject,
        preheader: config.preheader,
        body_html: bodyContent,
        body: bodyContent,
        templateId: config.templateId,
        trackClicks: config.trackClicks,
        utmTracking: config.utmTracking,
        utmSource: config.utmSource,
        utmMedium: config.utmMedium,
        utmCampaign: config.utmCampaign,
        addTags: config.addTags,
        attachments: config.attachments,
        configured: true,
      }
    });
    commitDraft();
    onClose();
  };

  const emailChannelReady = channelStatus?.email?.ready !== false;
  const charCount = stripHtmlForCount(config.body || '').length;
  const wordCount = countWords(config.body || '');

  if (!isOpen) return null;

  return (
    <>
    <WorkflowEditorPanelShell
      isOpen={isOpen}
      width={width ?? 640}
      overlayMode={overlayMode}
      chromeHeight={chromeHeight}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      className="border-l border-slate-200 bg-white"
      testId="email-action-composer"
    >
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Mail size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Send Email</h2>
            <p className="text-xs text-blue-100">Configure the email message sent by this workflow</p>
          </div>
        </div>
        <button
          onClick={handleRequestClose}
          className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          data-testid="email-action-close"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Channel Status Banner */}
      {!emailChannelReady && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="font-medium text-amber-800 text-sm">Email Service Not Configured</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Connect your email service to send emails from workflows.
              </p>
              {onNavigateToSettings && (
                <Button
                  size="sm"
                  className="mt-2 h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => onNavigateToSettings('communication')}
                >
                  <ArrowUpRight size={12} className="mr-1" />
                  Configure Email
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Tabs: Compose | Preview | Statistics */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('compose')}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all border-b-2",
            activeTab === 'compose'
              ? "border-blue-500 text-blue-600 bg-blue-50/50"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-composer-tab-compose"
        >
          <Edit3 size={16} />
          Compose
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all border-b-2",
            activeTab === 'preview'
              ? "border-blue-500 text-blue-600 bg-blue-50/50"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-composer-tab-preview"
        >
          <Eye size={16} />
          Preview
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all border-b-2",
            activeTab === 'statistics'
              ? "border-blue-500 text-blue-600 bg-blue-50/50"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-composer-tab-statistics"
        >
          <BarChart3 size={16} />
          Statistics
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'compose' ? (
          <div className="p-5 space-y-5">
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 font-medium">Action name</Label>
              <Input
                value={config.actionName}
                onChange={(e) => setConfig(prev => ({ ...prev, actionName: e.target.value }))}
                placeholder="Send Email"
                className="text-sm"
                data-testid="action-name-input"
              />
            </div>

            {/* Sender Information */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
                <User size={16} className="text-slate-400" />
                Sender Information
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">From Name</Label>
                  <MergeFieldInput
                    placeholder="Your Company"
                    value={config.fromName}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, fromName: value }))}
                    inputClassName="h-9 text-sm"
                    data-testid="from-name-input"
                    token={token}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">From Email</Label>
                  <MergeFieldInput
                    placeholder="noreply@example.com"
                    value={config.fromEmail}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, fromEmail: value }))}
                    inputClassName="h-9 text-sm"
                    data-testid="from-email-input"
                    token={token}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Reply-To</Label>
                  <MergeFieldInput
                    placeholder="Optional"
                    value={config.replyTo}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, replyTo: value }))}
                    inputClassName="h-9 text-sm"
                    data-testid="reply-to-input"
                    token={token}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">CC</Label>
                  <MergeFieldInput
                    placeholder="Optional"
                    value={config.cc}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, cc: value }))}
                    inputClassName="h-9 text-sm"
                    data-testid="cc-input"
                    token={token}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">BCC</Label>
                  <MergeFieldInput
                    placeholder="Optional"
                    value={config.bcc}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, bcc: value }))}
                    inputClassName="h-9 text-sm"
                    data-testid="bcc-input"
                    token={token}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 font-medium">To</Label>
              <MergeFieldInput
                placeholder="{{contact.email}}"
                value={config.to}
                onValueChange={(value) => setConfig(prev => ({ ...prev, to: value }))}
                inputClassName="text-sm"
                data-testid="to-input"
                token={token}
              />
              <p className="text-xs text-slate-500">Defaults to the contact email. Use custom values like {'{{contact.email}}'}.</p>
            </div>

            {/* Subject Line */}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 font-medium">Subject Line *</Label>
              <MergeFieldInput
                placeholder="Enter email subject..."
                value={config.subject}
                onValueChange={(value) => setConfig(prev => ({ ...prev, subject: value }))}
                inputClassName="text-sm"
                data-testid="subject-input"
                token={token}
              />
            </div>

            {/* Pre-header */}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 font-medium">(Optional) Pre-header</Label>
              <MergeFieldInput
                placeholder="This will be used as the preview text in some email clients."
                value={config.preheader}
                onValueChange={(value) => setConfig(prev => ({ ...prev, preheader: value }))}
                inputClassName="text-sm"
                data-testid="preheader-input"
                token={token}
              />
              <p className="text-xs text-slate-500">This will be used as the preview text that displays in some email clients.</p>
            </div>

            {/* Create Email Mode Selector */}
            <div className="space-y-3">
              <Label className="text-sm text-slate-700 font-medium">Create email</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setCreateMode('quick')}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left',
                    createMode === 'quick'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div className={cn(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    createMode === 'quick' ? 'border-blue-500' : 'border-slate-300'
                  )}>
                    {createMode === 'quick' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className={cn(
                    'text-sm font-medium',
                    createMode === 'quick' ? 'text-blue-700' : 'text-slate-600'
                  )}>Quick compose</span>
                </button>
                
                <button
                  onClick={() => setCreateMode('smart')}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left',
                    createMode === 'smart'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div className={cn(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    createMode === 'smart' ? 'border-blue-500' : 'border-slate-300'
                  )}>
                    {createMode === 'smart' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className={cn(
                    'text-sm font-medium',
                    createMode === 'smart' ? 'text-blue-700' : 'text-slate-600'
                  )}>From Smart Builder</span>
                </button>
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left w-full',
                    createMode === 'template'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div className={cn(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    createMode === 'template' ? 'border-blue-500' : 'border-slate-300'
                  )}>
                    {createMode === 'template' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className={cn(
                    'text-sm font-medium',
                    createMode === 'template' ? 'text-blue-700' : 'text-slate-600'
                  )}>Select existing template</span>
                </button>
                
                <TemplatePicker
                  isOpen={showTemplatePicker}
                  onClose={() => setShowTemplatePicker(false)}
                  onSelect={handleTemplateSelect}
                />
              </div>
            </div>

            {/* Email Body Editor */}
            <div className="space-y-1">
              <Label className="text-sm text-slate-700 font-medium">Type your message *</Label>

              <RichEmailEditor
                ref={richEditorRef}
                value={config.body}
                onChange={handleBodyChange}
                attachments={config.attachments}
                token={token}
                onWriteWithAIClick={() => setShowWriteWithAIModal(true)}
                footer={(
                  <div className="flex items-center justify-between pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowWriteWithAIModal(true)}
                      className="h-8 text-xs text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                      data-testid="write-with-ai-button"
                    >
                      <Wand2 size={14} className="mr-1.5" />
                      Write with AI
                    </Button>
                    <div className="text-xs text-slate-500">
                      {charCount.toLocaleString()} characters | {wordCount.toLocaleString()} words
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Additional Settings */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowAdditionalSettings(!showAdditionalSettings)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Settings size={16} className="text-slate-500" />
                  <span className="font-medium text-slate-700 text-sm">Additional settings</span>
                </div>
                <ChevronDown 
                  size={16} 
                  className={cn(
                    "text-slate-400 transition-transform",
                    showAdditionalSettings && "rotate-180"
                  )}
                />
              </button>
              
              {showAdditionalSettings && (
                <div className="p-4 space-y-4 border-t border-slate-200">
                  {/* Track Clicks */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">Track clicks</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Discover which Links were Clicked, how many times each Link were Clicked, and who did the Clicking.
                      </p>
                    </div>
                    <Switch
                      checked={config.trackClicks}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, trackClicks: checked }))}
                    />
                  </div>

                  {/* UTM Tracking */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">UTM Tracking</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        When Enabled, this Setting Automatically Adds the Default UTM Parameters to every Link in the Campaign.{' '}
                        <button className="text-blue-600 hover:underline">Click Here</button>
                      </p>
                    </div>
                    <Switch
                      checked={config.utmTracking}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, utmTracking: checked }))}
                    />
                  </div>

                  {/* Add Tags */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">Add Tags</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Automate adding Tags to Contacts depending on how they interact with your Campaigns.
                      </p>
                      {config.addTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {config.addTags.map((tag) => (
                            <Badge 
                              key={tag} 
                              variant="outline" 
                              className="bg-blue-50 text-blue-700 border-blue-200 pr-1 text-xs"
                            >
                              {tag}
                              <button 
                                onClick={() => setConfig(prev => ({
                                  ...prev,
                                  addTags: prev.addTags.filter(t => t !== tag)
                                }))}
                                className="ml-1 p-0.5 hover:bg-blue-100 rounded"
                              >
                                <X size={10} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Switch checked={config.addTags.length > 0} />
                  </div>
                </div>
              )}
            </div>

            {/* Test Email */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-700 font-medium">Test Emails *</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Test Emails"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTestEmail}
                  disabled={isSendingTest || !testEmail.trim()}
                >
                  {isSendingTest ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Test Mail'
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : activeTab === 'statistics' ? (
          /* Statistics Tab */
          <EmailStatisticsPanel stepId={step?.id || step?._id} />
        ) : (
          /* Preview Tab */
          <EmailPreviewPanel
            fromName={config.fromName}
            fromEmail={config.fromEmail}
            replyTo={config.replyTo}
            subject={config.subject}
            preheader={config.preheader}
            bodyHtml={config.body}
          />
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <Button variant="outline" onClick={handleRequestClose} data-testid="email-action-cancel">
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="email-action-save"
        >
          <Save size={16} className="mr-2" />
          Save
        </Button>
      </div>
    </div>
    </WorkflowEditorPanelShell>

    <WriteEmailWithAIModal
      isOpen={showWriteWithAIModal}
      onClose={() => setShowWriteWithAIModal(false)}
      onUseEmail={handleUseAIEmail}
      workflowId={workflowId}
      stepId={step?.id || step?._id}
      existingEmail={config.body}
      availableVariables={availableVariables}
      token={token}
      isPlainText={false}
    />
    </>
  );
};

export default EmailActionComposer;
